import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import GATracker from './components/GATracker';
import { trackTherapistReviewsEvents } from './utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;
console.log('API URL:', API);

// Configure axios for cross-origin authentication
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check for email/password auth token
      let emailToken = localStorage.getItem('access_token');
      
      // Check for Google OAuth session token
      const sessionToken = getSessionIdFromUrl();
      
      if (sessionToken) {
        localStorage.setItem('access_token', sessionToken);
        // Clean up URL by removing session_token parameter
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        // Update emailToken for consistency
        emailToken = sessionToken;
      }

      // Try authentication with available credentials
      let response = null;
      
      if (emailToken) {
        response = await axios.get(`${API}/auth/status`, {
          headers: { 'Authorization': `Bearer ${emailToken}` }
        });
      }
      
      if (response && response.data && response.data.authenticated) {
        setUser({
          id: response.data.user_id,
          email: response.data.email,
          name: response.data.name,
          auth_type: response.data.auth_type
        });
        console.log('✅ AuthProvider - User state set:', {
          id: response.data.user_id,
          email: response.data.email,
          name: response.data.name
        });
      }
    } catch (error) {
      console.error('🚨 AuthProvider - Auth check failed:', error);
      console.error('🚨 AuthProvider - Error status:', error.response?.status);
      console.error('🚨 AuthProvider - Error data:', error.response?.data);
      
      // Only clear tokens on actual auth failures (401/403), not network errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error('🚨 AuthProvider - CLEARING TOKENS due to 401/403');
        localStorage.removeItem('access_token');
        localStorage.removeItem('sessionId');
        setUser(null);
      } else {
        console.log('✅ AuthProvider - KEEPING TOKENS (non-auth error)');
      }
      // Keep tokens for network errors, server errors, etc.
    } finally {
      setLoading(false);
    }
  };

  const getSessionIdFromUrl = () => {
    // Check for session_token in URL parameters (from OAuth redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get('session_token');
    if (sessionToken) {
      return sessionToken;
    }
    
    return null;
  };

  const loginWithGoogle = async () => {
    try {
      console.log('Attempting Google OAuth...');
      
      // Track sign in attempt
      trackTherapistReviewsEvents.signIn('google');
      
      // Call backend to get Google OAuth URL
      const response = await axios.get(`${API}/auth/google`);
      console.log('OAuth response:', response.data);
      
      if (response.data.authorization_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.authorization_url;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      console.error('Google OAuth error:', error);
      console.error('Error details:', error.response?.data);
      
      if (error.response?.status === 500) {
        alert('Google sign-in is not configured yet. Please use email registration for now.');
      } else {
        alert(`Google sign-in error: ${error.message}. Please try email registration instead.`);
      }
    }
  };

  const logout = () => {
    // Track sign out
    trackTherapistReviewsEvents.signOut();
    
    localStorage.removeItem('access_token');
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loginWithGoogle, logout, loading, checkAuthStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// User Avatar Component
const UserAvatar = ({ user, size = 'default', showDropdown = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { logout } = useAuth();
  const dropdownRef = useRef(null);
  
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };
  
  const sizeClasses = {
    small: 'w-8 h-8 text-xs',
    default: 'w-10 h-10 text-sm',
    large: 'w-12 h-12 text-base'
  };
  
  const toggleDropdown = () => {
    if (showDropdown) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className={`${sizeClasses[size]} bg-[#3B82F6] text-white rounded-full flex items-center justify-center font-semibold cursor-pointer hover:bg-[#2563EB] transition-colors`}
        onClick={toggleDropdown}
      >
        {getInitials(user?.name)}
      </div>
      
      {showDropdown && isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
              {user?.auth_type === 'google' ? 'Google Account' : 'Email Account'}
            </span>
          </div>
          
          <a href="/account" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            My Account
          </a>
          
          <a href="/account?tab=reviews" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            My Reviews
          </a>
          
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button 
              onClick={logout}
              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Star Rating Component
const StarRating = ({ rating, size = 'small' }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6'
  };
  
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(
        <svg key={i} className={`${sizeClasses[size]} fill-current`} style={{color: '#f6af3b'}} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    } else if (i === fullStars && hasHalfStar) {
      stars.push(
        <svg key={i} className={`${sizeClasses[size]} `} style={{color: '#f6af3b'}} viewBox="0 0 20 20">
          <defs>
            <linearGradient id={`halfStarGradient-${i}`}>
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="rgb(156 163 175)" />
            </linearGradient>
          </defs>
          <path fill={`url(#halfStarGradient-${i})`} d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    } else {
      stars.push(
        <svg key={i} className={`${sizeClasses[size]} text-gray-300 fill-current`} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
  }
  
  return <div className="flex items-center">{stars}</div>;
};

// Pagination Component
const Pagination = ({ pagination, onPageChange, searchParams }) => {
  const { current_page, total_pages, has_next, has_previous } = pagination;
  
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (total_pages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination with ellipsis
      if (current_page <= 3) {
        // Show: 1 2 3 4 ... last
        pages.push(1, 2, 3, 4, '...', total_pages);
      } else if (current_page >= total_pages - 2) {
        // Show: 1 ... (last-3) (last-2) (last-1) last
        pages.push(1, '...', total_pages - 3, total_pages - 2, total_pages - 1, total_pages);
      } else {
        // Show: 1 ... (current-1) current (current+1) ... last
        pages.push(1, '...', current_page - 1, current_page, current_page + 1, '...', total_pages);
      }
    }
    
    return pages;
  };

  const handlePageClick = (page) => {
    if (page === '...' || page === current_page) return;
    onPageChange(page);
  };

  if (total_pages <= 1) return null;

  return (
    <>
      {/* Mobile pagination: just prev/next + page indicator */}
      <div className="flex sm:hidden items-center justify-center space-x-3">
        <button
          onClick={() => handlePageClick(1)}
          disabled={current_page === 1}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          title="First page"
        >
          &lt;&lt;
        </button>
        <button
          onClick={() => handlePageClick(current_page - 1)}
          disabled={!has_previous}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          title="Previous page"
        >
          &lt;
        </button>
        <span className="text-sm text-gray-700 px-1">
          {current_page} / {total_pages}
        </span>
        <button
          onClick={() => handlePageClick(current_page + 1)}
          disabled={!has_next}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          title="Next page"
        >
          &gt;
        </button>
        <button
          onClick={() => handlePageClick(total_pages)}
          disabled={current_page === total_pages}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          title="Last page"
        >
          &gt;&gt;
        </button>
      </div>

      {/* Desktop pagination: full numbered buttons */}
      <div className="hidden sm:flex items-center justify-center space-x-2 mt-8">
        <button
          onClick={() => handlePageClick(1)}
          disabled={current_page === 1}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="First page"
        >
          &lt;&lt;
        </button>
        <button
          onClick={() => handlePageClick(current_page - 1)}
          disabled={!has_previous}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Previous page"
        >
          &lt;
        </button>
        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => handlePageClick(page)}
            disabled={page === '...'}
            className={`px-3 py-2 rounded-xl border ${
              page === current_page
                ? 'bg-[#3B82F6] text-white border-[#3B82F6]'
                : page === '...'
                ? 'bg-white border-gray-300 text-gray-400 cursor-not-allowed'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => handlePageClick(current_page + 1)}
          disabled={!has_next}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Next page"
        >
          &gt;
        </button>
        <button
          onClick={() => handlePageClick(total_pages)}
          disabled={current_page === total_pages}
          className="px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Last page"
        >
          &gt;&gt;
        </button>
      </div>
    </>
  );
};

// Registration Component
const RegistrationPage = () => {
  const { loginWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    name: 'User', // Default name since field is hidden
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    isValid: false
  });

  // Password validation function (8-4 rule)
  const validatePassword = (password) => {
    const validation = {
      minLength: password.length >= 8,
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    validation.isValid = validation.minLength && validation.hasLower && validation.hasUpper && validation.hasNumber && validation.hasSpecial;
    return validation;
  };

  // Handle password input change with validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({...formData, password: newPassword});
    setPasswordValidation(validatePassword(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validate password meets 8-4 rule
    if (!passwordValidation.isValid) {
      setError('Please ensure your password meets all requirements.');
      setLoading(false);
      return;
    }

    // Validate that passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please ensure both password fields are identical.');
      setLoading(false);
      return;
    }

    try {
      // Track sign up attempt
      trackTherapistReviewsEvents.signUp('email');
      
      // Send only the necessary fields to the API (excluding confirmPassword)
      const { confirmPassword, ...registrationData } = formData;
      const response = await axios.post(`${API}/auth/register`, registrationData);
      setMessage(response.data.message);
      setFormData({ name: 'User', email: '', password: '', confirmPassword: '' });
    } catch (error) {
      setError(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join Therapist Reviews to submit anonymous reviews
          </p>
          <p className="mt-1 text-center text-sm text-gray-500">
            Your credentials will not be displayed with your rating.
          </p>
        </div>


        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-xl shadow-sm -space-y-px">
            {/* Hidden name field - kept for backend compatibility */}
            <input
              type="hidden"
              name="name"
              value={formData.name}
            />
            <div>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-xl focus:outline-none focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <input
                type="password"
                required
                value={formData.password}
                onChange={handlePasswordChange}
                className={`relative block w-full px-3 py-2 border placeholder-gray-500 text-gray-900 focus:outline-none focus:z-10 sm:text-sm ${
                  formData.password && !passwordValidation.isValid 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-[#3B82F6] focus:border-[#3B82F6]'
                }`}
                placeholder="Password"
              />
            </div>
            
            {/* Password Requirements */}
            {formData.password && (
              <div className="px-3 py-2 bg-gray-50 text-xs space-y-1">
                <div className="font-medium text-gray-700 mb-1">Password must contain:</div>
                <div className={`flex items-center ${passwordValidation.minLength ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-1">{passwordValidation.minLength ? '✓' : '✗'}</span>
                  At least 8 characters
                </div>
                <div className={`flex items-center ${passwordValidation.hasLower ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-1">{passwordValidation.hasLower ? '✓' : '✗'}</span>
                  One lowercase letter (a-z)
                </div>
                <div className={`flex items-center ${passwordValidation.hasUpper ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-1">{passwordValidation.hasUpper ? '✓' : '✗'}</span>
                  One uppercase letter (A-Z)
                </div>
                <div className={`flex items-center ${passwordValidation.hasNumber ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-1">{passwordValidation.hasNumber ? '✓' : '✗'}</span>
                  One number (0-9)
                </div>
                <div className={`flex items-center ${passwordValidation.hasSpecial ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-1">{passwordValidation.hasSpecial ? '✓' : '✗'}</span>
                  One special character (!@#$%^&*)
                </div>
              </div>
            )}
            <div>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-xl focus:outline-none focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:z-10 sm:text-sm"
                placeholder="Confirm Password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-[#3B82F6] hover:bg-[#2563EB] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3B82F6] disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>

          {/* Or Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gradient-to-br from-orange-50 to-red-100 text-gray-500">Or</span>
            </div>
          </div>

          {/* Google Login */}
          <div>
            <button
              type="button"
              onClick={loginWithGoogle}
              className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3B82F6]"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
          </div>

          <div className="text-center">
            <a href="/login" className="text-[#3B82F6] hover:text-[#2563EB]">
              Already have an account? Sign in
            </a>
          </div>
        </form>

        {message && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-green-800 text-sm">{message}</p>
            <div className="mt-2">
              <a href="/login" className="text-green-700 hover:text-green-800 font-medium text-sm underline">
                → Go to Sign In Page
              </a>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Login Component
const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginWithGoogle, checkAuthStatus } = useAuth();
  const navigate = useNavigate();

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Track sign in attempt
    trackTherapistReviewsEvents.signIn('email');

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      localStorage.setItem('access_token', response.data.access_token);
      await checkAuthStatus();
      navigate('/');
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your Therapist Reviews account
          </p>
        </div>

        {/* Email/Password Login */}
        <form className="mt-8 space-y-6" onSubmit={handleEmailLogin}>
          <div className="rounded-xl shadow-sm -space-y-px">
            <div>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-xl focus:outline-none focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-xl focus:outline-none focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div className="text-right">
            <a href="/forgot-password" className="text-sm text-gray-600 hover:text-[#3B82F6]">
              Forgot your password?
            </a>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-[#3B82F6] hover:bg-[#2563EB] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3B82F6] disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in with Email'}
            </button>
          </div>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gradient-to-br from-orange-50 to-red-100 text-gray-500">Or</span>
          </div>
        </div>

        {/* Google Login */}
        <div>
          <button
            type="button"
            onClick={loginWithGoogle}
            className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3B82F6]"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>

        <div className="text-center">
          <a href="/register" className="text-[#3B82F6] hover:text-[#2563EB]">
            Don't have an account? Create one
          </a>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Forgot Password Component
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(response.data.message);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-[#3B82F6] focus:border-[#3B82F6] sm:text-sm"
              placeholder="Email address"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-[#3B82F6] hover:bg-[#2563EB] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3B82F6] disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
        </form>

        <div className="text-center">
          <a href="/login" className="text-[#3B82F6] hover:text-[#2563EB]">
            Back to Sign In
          </a>
        </div>

        {message && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-green-800 text-sm">{message}</p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Email Verification Component
const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    console.log('🔍 EmailVerificationPage - Found token:', token);
    if (token) {
      console.log('📤 EmailVerificationPage - Starting verification with token:', token);
      verifyEmail(token);
    } else {
      console.log('❌ EmailVerificationPage - No token found in URL');
      setStatus('error');
      setMessage('Invalid verification link');
    }
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      console.log('📤 EmailVerificationPage - Sending verification request with token:', token);
      const response = await axios.post(`${API}/auth/verify-email`, { token });
      console.log('✅ EmailVerificationPage - Verification response:', response.data);
      setStatus('success');
      setMessage(response.data.message);
    } catch (error) {
      console.error('❌ EmailVerificationPage - Email verification failed:', error);
      console.error('Error response:', error.response?.data);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full text-center">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-8">Email Verification</h2>
        
        {status === 'verifying' && (
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your email...</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-800 mb-4">{message}</p>
            <a href="/login" className="bg-[#3B82F6] text-white px-6 py-2 rounded-xl hover:bg-[#2563EB]">
              Go to Sign In
            </a>
          </div>
        )}
        
        {status === 'error' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-800 mb-4">{message}</p>
            <a href="/register" className="bg-[#3B82F6] text-white px-6 py-2 rounded-xl hover:bg-[#2563EB]">
              Back to Registration
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// Reset Password Component
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const resetToken = searchParams.get('reset');
    if (resetToken) {
      setToken(resetToken);
    } else {
      setError('Invalid or missing reset token');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.newPassword
      });
      setMessage(response.data.message);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Set new password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password below
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-xl shadow-sm -space-y-px">
            <div>
              <input
                type="password"
                required
                value={formData.newPassword}
                onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-xl focus:outline-none focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:z-10 sm:text-sm"
                placeholder="New password (min 8 characters)"
                minLength="8"
              />
            </div>
            <div>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-xl focus:outline-none focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:z-10 sm:text-sm"
                placeholder="Confirm new password"
                minLength="8"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !token}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-[#3B82F6] hover:bg-[#2563EB] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3B82F6] disabled:opacity-50"
            >
              {loading ? 'Updating Password...' : 'Update Password'}
            </button>
          </div>
        </form>

        <div className="text-center">
          <a href="/login" className="text-[#3B82F6] hover:text-[#2563EB]">
            Back to Sign In
          </a>
        </div>

        {message && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-green-800 text-sm">{message}</p>
            <p className="text-green-600 text-xs mt-1">Redirecting to login...</p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Available therapy specialties (50 specializations)
const THERAPY_SPECIALTIES = [
  'Anxiety', 'Trauma', 'PTSD', 'Relationship Issues', 'Depression',
  'Life Transitions', 'Self Esteem', 'ADHD', 'Grief', 'Stress',
  'Addiction', 'Coping Skills', 'Child', 'Parenting', 'Family Conflict',
  'Mood Disorders', 'LGBTQ+', 'Marital', 'Premarital', 'Women\'s Issues',
  'Pregnancy', 'Men\'s Issues', 'Autism', 'Emotional Disturbance', 'Eating Disorders',
  'Obsessive-Compulsive (OCD)', 'Spirituality', 'Sex Therapy', 'Sexual Abuse', 'Anger Management',
  'Borderline Personality (BPD)', 'Behavioural Issues', 'Racial Identity', 'Chronic Illness', 'Life Coaching',
  'Substance Use', 'Domestic Violence', 'Chronic Pain', 'Divorce', 'Education',
  'Learning Disabilities', 'Transgender', 'Codependency', 'Career Guidance', 'First Responders',
  'Sports Performance', 'Self Harming', 'Game Addiction', 'Hoarding', 'Traumatic Brain Injury'
];

// Specialty Checkbox Component
const SpecialtyCheckbox = ({ specialty, isSelected, onToggle }) => {
  return (
    <button
      type="button"
      onClick={() => onToggle(specialty)}
      className={`flex items-center justify-center w-full h-16 px-4 py-3 rounded-2xl border-4 transition-all duration-200 text-sm font-medium ${
        isSelected
          ? 'bg-blue-500 border-blue-500 text-white'
          : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300'
      }`}
    >
      <span className="text-center text-sm leading-tight">{specialty}</span>
    </button>
  );
};

// Landing Page Component (Clean search only)
const LandingPage = () => {
  const [mainSearchQuery, setMainSearchQuery] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle specialty toggle
  const toggleSpecialty = (specialty) => {
    setSelectedSpecialties(prev => 
      prev.includes(specialty) 
        ? prev.filter(s => s !== specialty)
        : [...prev, specialty]
    );
  };

  // Use useEffect to run verification only ONCE when component mounts
  useEffect(() => {
    // Check if we've already processed this verification
    const processed = sessionStorage.getItem('verification_processed');
    
    if (window.location.hash && !processed) {
      console.log('Hash detected:', window.location.hash);
      
      if (window.location.hash.includes('verify=')) {
        const token = window.location.hash.split('verify=')[1];
        console.log('Token found:', token);
        
        // Mark as processed IMMEDIATELY to prevent re-runs
        sessionStorage.setItem('verification_processed', 'true');
        
        // Set loading state
        setIsLoading(true);
        setMessage("Verifying your email...");
        
        // Verify the email
        axios.post(`${API}/auth/verify-email`, { token })
          .then(res => {
            console.log('Verification successful:', res.data);
            setMessage("Congratulations! You have successfully verified your email address!");
            
            // Store token and log user in
            if (res.data.access_token) {
              localStorage.setItem('access_token', res.data.access_token);
              
              // Update user state directly (following CLAUDE.md auth fix pattern)
              setUser({
                id: res.data.user.id,
                email: res.data.user.email,
                name: res.data.user.name,
                auth_type: res.data.user.auth_type
              });
              
              console.log('✅ User logged in after verification:', res.data.user);
            }
            
            // Clean up the URL
            window.history.replaceState(null, '', '/');
            
            // Navigate after a brief delay
            setTimeout(() => {
              sessionStorage.removeItem('verification_processed');
              navigate('/'); // Stay on home page where they'll be logged in
            }, 2000);
          })
          .catch(err => {
            console.error('FULL ERROR OBJECT:', err);
            console.error('Error message:', err.message);
            console.error('Error code:', err.code);
            console.error('Error response exists?:', !!err.response);
            
            if (err.response) {
              console.error('Response status:', err.response.status);
              console.error('Response data:', err.response.data);
              console.error('Response headers:', err.response.headers);
            } else if (err.request) {
              console.error('Request was made but no response:', err.request);
            } else {
              console.error('Error setting up request:', err.message);
            }
            
            // Check if it's a CORS error
            if (!err.response) {
              console.error('This might be a CORS error - no response received');
            }
            
            setMessage(err.response?.data?.detail || "Verification failed. Please try again.");
            setIsLoading(false);
            
            // Clean up
            window.history.replaceState(null, '', '/');
            sessionStorage.removeItem('verification_processed');
          });
      }
    }
  }, []); // Empty dependency array = runs only once on mount

  // Clear suggestions when location changes (e.g., navigating to therapist profile)
  useEffect(() => {
    console.log('🧹 LandingPage: Clearing suggestions due to location change:', location.pathname);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  }, [location.pathname]);

  // Also clear suggestions on component mount
  useEffect(() => {
    console.log('🏠 LandingPage: Component mounted, clearing suggestions');
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  }, []);

  console.log('🏠 LandingPage loaded, current URL:', window.location.href);
  console.log('🏠 Search params:', window.location.search);
  // Force Railway redeploy - timestamp: 2025-09-09

  // Password reset handler for /#reset= pattern (same as email verification)
  useEffect(() => {
    console.log('🔍 LandingPage: Checking for reset token...');
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 Hash:', window.location.hash);
    
    // Check if we already processed this reset
    const processed = sessionStorage.getItem('reset_processed');
    
    if (window.location.hash && !processed) {
      console.log('🔍 Hash detected:', window.location.hash);
      
      if (window.location.hash.includes('reset=')) {
        const resetToken = window.location.hash.split('reset=')[1];
        console.log('🔑 Reset token found:', resetToken);
        
        // Mark as processed IMMEDIATELY to prevent re-runs
        sessionStorage.setItem('reset_processed', 'true');
        
        // Clean up URL and redirect to reset page
        window.history.replaceState(null, '', '/');
        navigate(`/reset-password?reset=${resetToken}`);
      }
    }
  }, [navigate]);

  // Email verification handler for /?verify= pattern
  useEffect(() => {
    // Check URL first
    const verifyToken = searchParams.get('verify');
    
    // If not in URL, check if we saved it
    const savedToken = sessionStorage.getItem('pendingVerification');
    
    const tokenToUse = verifyToken || savedToken;
    
    console.log('🔍 Checking for verify token from URL:', verifyToken);
    console.log('🔍 Checking for saved token:', savedToken);
    console.log('🔍 Token to use:', tokenToUse);
    
    if (tokenToUse) {
      console.log('✅ Found token, starting verification...');
      
      const verifyEmail = async () => {
        try {
          console.log('📤 Sending to:', `${API}/auth/verify-email`);
          console.log('📤 With token:', tokenToUse);
          
          const response = await axios.post(`${API}/auth/verify-email`, {
            token: tokenToUse
          });
          
          console.log('✅ SUCCESS! Response:', response.data);
          
          // Handle successful verification - extract token and log user in
          if (response.data.access_token) {
            localStorage.setItem('access_token', response.data.access_token);
            
            // Update user state to log them in (following CLAUDE.md auth fix pattern)
            setUser({
              id: response.data.user.id,
              email: response.data.user.email,
              name: response.data.user.name,
              auth_type: response.data.user.auth_type
            });
            
            console.log('✅ User logged in after verification:', response.data.user);
          }
          
          // Clear the saved token after successful verification
          sessionStorage.removeItem('pendingVerification');
          
          setVerificationStatus('success');
          setSearchParams({});
          setTimeout(() => {
            setVerificationStatus(null);
          }, 5000);
          
        } catch (error) {
          console.error('❌ VERIFICATION FAILED!');
          console.error('Error details:', error.response?.data);
          console.error('Error status:', error.response?.status);
          
          // Clear the saved token on error too
          sessionStorage.removeItem('pendingVerification');
          
          if (error.response?.status === 400) {
            setVerificationStatus('invalid');
            alert('Verification failed: ' + (error.response?.data?.detail || 'Invalid token'));
          } else {
            setVerificationStatus('error');
            alert('Verification error: ' + (error.response?.data?.detail || 'Unknown error'));
          }
        }
      };
      
      verifyEmail();
    }
  }, [searchParams, setSearchParams]);

  // Debounced search function
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (mainSearchQuery.trim().length >= 2) {
        fetchSuggestions(mainSearchQuery.trim());
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [mainSearchQuery]);

  const fetchSuggestions = async (query) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/therapists/search?name=${encodeURIComponent(query)}&limit=8`);
      setSuggestions(response.data.therapists || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (therapist) => {
    console.log('🖱️ LandingPage: Suggestion clicked, navigating to therapist:', therapist.id);
    // Immediately clear all suggestion state
    setSearchName('');
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    setLoading(false);
    
    // Navigate after a brief delay to ensure state is cleared
    setTimeout(() => {
      navigate(`/therapist/${therapist.id}`);
    }, 10);
  };

  const handleInputKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for click events
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    // SIMPLIFIED: Just check what fields have values and use them
    const params = new URLSearchParams();
    let hasSearchCriteria = false;

    // Main search query (always check first)
    if (mainSearchQuery.trim()) {
      params.append('name', mainSearchQuery.trim());
      hasSearchCriteria = true;
      // Track search event
      trackTherapistReviewsEvents.searchTherapist(mainSearchQuery.trim(), '');
    }
    // Advanced search fields (only if main search is empty)
    else if (isAdvancedSearchOpen) {
      if (searchName.trim()) {
        params.append('name', searchName.trim());
        hasSearchCriteria = true;
      }
      if (searchCity.trim()) {
        params.append('city', searchCity.trim());
        hasSearchCriteria = true;
      }
      if (selectedSpecialties && selectedSpecialties.length > 0) {
        selectedSpecialties.forEach(specialty => params.append('specialty', specialty));
        hasSearchCriteria = true;
      }
      // Track search event
      trackTherapistReviewsEvents.searchTherapist(searchName.trim(), searchCity.trim());
    }

    if (!hasSearchCriteria) {
      alert('Please enter a search query');
      return;
    }

    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Show message if we have one */}
      {message && (
        <div style={{
          padding: '20px',
          backgroundColor: message.includes('success') ? '#d4edda' : '#f8d7da',
          color: message.includes('success') ? '#155724' : '#721c24',
          borderRadius: '5px',
          margin: '20px',
          textAlign: 'center'
        }}>
          {message}
        </div>
      )}
      
      {/* Header */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity min-w-0" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="Therapist Reviews Logo" className="w-8 h-8 flex-shrink-0" />
              <h1 className="text-base sm:text-xl font-semibold text-gray-900 whitespace-nowrap">Therapist Reviews</h1>
            </div>
            <div className="flex items-center space-x-3 flex-shrink-0">
              {user ? (
                <UserAvatar user={user} showDropdown={true} />
              ) : (
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <a href="/login" className="text-gray-700 hover:text-gray-900 text-sm font-medium whitespace-nowrap">Log in</a>
                  <a href="/register" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-3 py-2 rounded-2xl text-sm font-medium transition-colors whitespace-nowrap">
                    Sign up
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Find a therapist you can trust
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Discover, read, and write reviews
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-gray-50 rounded-2xl p-6 sm:p-12 mb-12 -mx-2">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={mainSearchQuery}
                  onChange={(e) => setMainSearchQuery(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleInputBlur}
                  onFocus={() => {
                    if (suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="Search therapist name or city..."
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-colors"
                  autoComplete="off"
                />
                
                {/* Suggestions Dropdown */}
                {showSuggestions && (suggestions.length > 0 || loading) && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-lg z-50 mt-1 max-h-80 overflow-y-auto">
                    {loading && (
                      <div className="p-4 text-center text-gray-500">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                        <span className="ml-2">Searching...</span>
                      </div>
                    )}
                    
                    {!loading && suggestions.map((therapist, index) => (
                      <div
                        key={therapist.id}
                        onClick={() => handleSuggestionClick(therapist)}
                        className={`p-4 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                          index === selectedIndex ? 'bg-green-50 border-green-200' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{therapist.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="inline-flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {therapist.city}
                              </span>
                            </p>
                            {therapist.specialties && therapist.specialties.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {therapist.specialties.slice(0, 3).join(', ')}
                                {therapist.specialties.length > 3 && '...'}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="flex items-center space-x-1">
                              <StarRating rating={therapist.avg_overall} size="small" />
                              <span className="text-sm text-gray-500">
                                ({therapist.total_reviews})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {!loading && suggestions.length === 0 && searchName.trim().length >= 2 && (
                      <div className="p-4 text-center text-gray-500">
                        No therapists found matching "{searchName}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={isAdvancedSearchOpen || !mainSearchQuery.trim()}
                className="px-8 py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Search</span>
              </button>
            </div>
            
            {/* Advanced Search Toggle */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
                className="text-gray-500 hover:text-gray-700 text-sm underline"
              >
                {isAdvancedSearchOpen ? 'Hide advanced options' : 'Advanced search options'}
              </button>
            </div>
            
{isAdvancedSearchOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Therapist Name
                </label>
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Enter therapist name..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="Enter city..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-colors"
                />
              </div>

              {/* Specialty Selection - Full Width */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Specialties
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {THERAPY_SPECIALTIES.map((specialty) => (
                    <SpecialtyCheckbox
                      key={specialty}
                      specialty={specialty}
                      isSelected={selectedSpecialties.includes(specialty)}
                      onToggle={toggleSpecialty}
                    />
                  ))}
                </div>
                {selectedSpecialties.length > 0 && (
                  <div className="mt-3 text-sm text-gray-600">
                    Selected: {selectedSpecialties.length} specialt{selectedSpecialties.length === 1 ? 'y' : 'ies'}
                  </div>
                )}

                {/* Advanced Search Button */}
                <div className="mt-6 text-center">
                  <button
                    type="submit"
                    className="px-12 py-4 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-2xl transition-colors flex items-center justify-center space-x-2 mx-auto"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Search with Advanced Options</span>
                  </button>
                </div>
              </div>
            </div>
            )}
          </form>
        </div>

        {/* Trust Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Anonymous</h3>
            <p className="text-gray-600 text-sm">Your reviews are always private.</p>
          </div>
          
          <div className="p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Discover</h3>
            <p className="text-gray-600 text-sm">Find the right therapist from thousands of options.</p>
          </div>
          
          <div className="p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Instant</h3>
            <p className="text-gray-600 text-sm">Search therapist profiles and reviews instantly.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Search Results Page Component
const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Search form state
  const [searchName, setSearchName] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Get search parameters from URL
  const name = searchParams.get('name') || '';
  const city = searchParams.get('city') || '';
  const specialties = searchParams.getAll('specialty') || [];
  const page = parseInt(searchParams.get('page')) || 1;

  // Single useEffect to handle searching
  useEffect(() => {
    let isMounted = true;

    const doSearch = async () => {
      // Don't search if no parameters
      if (!name && !city && specialties.length === 0) {
        return;
      }

      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        if (name) params.append('name', name);
        if (city) params.append('city', city);
        specialties.forEach(s => params.append('specialty', s));
        params.append('page', page.toString());
        params.append('limit', '10');

        const response = await axios.get(`${API}/therapists/search?${params}`);

        if (isMounted) {
          setResults(response.data.therapists || []);
          setPagination(response.data.pagination);
        }
      } catch (err) {
        if (isMounted) {
          setError('Error searching therapists. Please try again.');
          console.error('Search error:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    doSearch();

    return () => {
      isMounted = false;
    };
  }, [name, city, page, specialties.join(',')]); // Include specialties to re-fetch when they change

  // Initialize search form with URL parameters
  useEffect(() => {
    setSearchName(name);
    setSearchCity(city);
  }, [name, city]);

  // Debounced search for suggestions
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (hasUserInteracted && searchName.trim().length >= 2) {
        fetchSuggestions(searchName.trim());
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchName, hasUserInteracted]);


  const handlePageChange = (newPage) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    if (city) params.append('city', city);
    specialties.forEach(s => params.append('specialty', s));
    params.append('page', newPage.toString());

    navigate(`/search?${params.toString()}`);
  };

  const viewProfile = (therapistId) => {
    navigate(`/therapist/${therapistId}`);
  };

  const newSearch = () => {
    navigate('/');
  };

  const handleSearchFromResults = (e) => {
    e.preventDefault();

    if (!searchName.trim() && !searchCity.trim()) {
      alert('Please enter a therapist name or city to search');
      return;
    }

    // Track search event
    trackTherapistReviewsEvents.searchTherapist(searchName.trim(), searchCity.trim());

    // Navigate to search results page with new URL parameters
    const params = new URLSearchParams();
    if (searchName.trim()) params.append('name', searchName.trim());
    if (searchCity.trim()) params.append('city', searchCity.trim());

    navigate(`/search?${params.toString()}`);
  };

  const fetchSuggestions = async (query) => {
    setSuggestionLoading(true);
    try {
      const response = await axios.get(`${API}/therapists/search?name=${encodeURIComponent(query)}&limit=8`);
      setSuggestions(response.data.therapists || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleSuggestionClick = (therapist) => {
    console.log('🖱️ Suggestion clicked, navigating to therapist:', therapist.id);
    // Immediately clear all suggestion state
    setSearchName('');
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    if (typeof setLoading === 'function') setLoading(false);
    if (typeof setSuggestionLoading === 'function') setSuggestionLoading(false);
    
    // Navigate after a brief delay to ensure state is cleared
    setTimeout(() => {
      navigate(`/therapist/${therapist.id}`);
    }, 10);
  };

  const handleInputKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for click events
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Searching therapists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity min-w-0" onClick={newSearch}>
              <img src="/logo.png" alt="Therapist Reviews Logo" className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0" />
              <h1 className="text-base sm:text-2xl font-bold text-gray-900 whitespace-nowrap">Therapist Reviews</h1>
            </div>
            {user ? (
              <UserAvatar user={user} showDropdown={true} />
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="bg-[#3B82F6] text-white px-3 sm:px-4 py-2 rounded-2xl hover:bg-[#2563EB] transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-6 mb-3 sm:mb-6">
          <form onSubmit={handleSearchFromResults}>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => {
                    setHasUserInteracted(true);
                    setSearchName(e.target.value);
                  }}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleInputBlur}
                  onFocus={() => {
                    setHasUserInteracted(true);
                    if (suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="Search therapist name..."
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 text-base sm:text-lg border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-colors"
                  autoComplete="off"
                />

                {/* Suggestions Dropdown */}
                {showSuggestions && (suggestions.length > 0 || suggestionLoading) && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-lg z-50 mt-1 max-h-80 overflow-y-auto">
                    {suggestionLoading && (
                      <div className="p-4 text-center text-gray-500">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        <span className="ml-2">Searching...</span>
                      </div>
                    )}

                    {!suggestionLoading && suggestions.map((therapist, index) => (
                      <div
                        key={therapist.id}
                        onClick={() => handleSuggestionClick(therapist)}
                        className={`p-4 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                          index === selectedIndex ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{therapist.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="inline-flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {therapist.city}
                              </span>
                            </p>
                            {therapist.specialties && therapist.specialties.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {therapist.specialties.slice(0, 3).join(', ')}
                                {therapist.specialties.length > 3 && '...'}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="flex items-center space-x-1">
                              <StarRating rating={therapist.avg_overall} size="small" />
                              <span className="text-sm text-gray-500">
                                ({therapist.total_reviews})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {!suggestionLoading && suggestions.length === 0 && searchName.trim().length >= 2 && (
                      <div className="p-4 text-center text-gray-500">
                        No therapists found matching "{searchName}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="Search city..."
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 text-base sm:text-lg border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-colors"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-2 sm:px-8 sm:py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-2xl transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Search</span>
              </button>
            </div>
          </form>
        </div>

        {/* Search Query Display */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Search Results</h2>
          <div className="flex items-center space-x-4 text-gray-600">
            {name && (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                Name: {name}
              </span>
            )}
            {city && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                City: {city}
              </span>
            )}
          </div>
        </div>

        {/* Results */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {results.length === 0 && !error ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33"/>
            </svg>
            <p className="text-gray-500 text-lg">No therapists found matching your search criteria.</p>
            <button
              onClick={newSearch}
              className="mt-4 bg-[#3B82F6] text-white px-6 py-2 rounded-2xl hover:bg-[#2563EB] transition-colors"
            >
              Try a Different Search
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Results List */}
            {results.map((therapist) => (
              <div
                key={therapist.id}
                className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => viewProfile(therapist.id)}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                        {therapist.name}
                      </h3>
                      
                      <div className="flex items-center text-gray-600 mb-2">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {therapist.city}
                      </div>
                      
                      {therapist.specialties && therapist.specialties.length > 0 && (
                        <div className="mb-3">
                          <span className="text-gray-500 font-medium">Specialties: </span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {therapist.specialties.map((specialty, index) => (
                              <span key={index} className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-2 rounded-lg">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <StarRating rating={therapist.avg_overall} size="small" />
                          <span className="text-sm text-gray-600 whitespace-nowrap">
                            {therapist.avg_overall.toFixed(1)} ({therapist.total_reviews} reviews)
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="sm:ml-4 self-end sm:self-start flex-shrink-0">
                      <button className="text-[#3B82F6] hover:text-[#2563EB] font-medium text-sm sm:text-base">
                        View Profile →
                      </button>
                    </div>
                  </div>
                </div>
              ))}

            {/* Pagination */}
            {pagination && (
              <div className="bg-white rounded-2xl shadow-sm px-4 py-4 mt-4">
                <div className="text-sm text-gray-700 text-center mb-3">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_results)} of {' '}
                  {pagination.total_results} results
                </div>
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  searchParams={searchParams}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Therapist Profile Component
const TherapistProfile = ({ therapistId }) => {
  const [therapist, setTherapist] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const { user, loading: authLoading, checkAuthStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Force auth check when component mounts
  useEffect(() => {
    if (!user && localStorage.getItem('access_token')) {
      checkAuthStatus();
    }
  }, [user, checkAuthStatus]);

  // Search functionality state
  const [searchName, setSearchName] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    fetchTherapistData();
  }, [therapistId]);

  // Clear suggestions when location changes (e.g., navigating between therapist profiles)
  useEffect(() => {
    console.log('🧹 TherapistProfile: Clearing suggestions due to location change:', location.pathname);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  }, [location.pathname]);

  // Clear suggestions on component mount
  useEffect(() => {
    console.log('👤 TherapistProfile: Component mounted for therapist', therapistId, ', clearing suggestions');
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  }, [therapistId]);

  // Debug auth status
  useEffect(() => {
    const debugAuth = async () => {
      const token = localStorage.getItem('access_token');
      console.log('🔍 Debug Auth - Token exists:', !!token);
      console.log('🔍 Debug Auth - Token value:', token);
      
      try {
        const response = await axios.get(`${API}/auth/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ Auth status response:', response.data);
      } catch (error) {
        console.log('❌ Auth status error:', error.response?.status, error.response?.data);
        console.log('❌ Full error:', error);
      }
    };
    debugAuth();
  }, []);

  const fetchTherapistData = async () => {
    try {
      // GET THE TOKEN AND ADD TO HEADERS
      const token = localStorage.getItem('access_token');
      const config = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};
      
      const response = await axios.get(`${API}/therapists/${therapistId}`, config);
      setTherapist(response.data.therapist);
      setReviews(response.data.reviews);
      
      // Track therapist profile view
      trackTherapistReviewsEvents.viewTherapistProfile(therapistId, response.data.therapist.name);
    } catch (error) {
      console.error('Error fetching therapist data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWriteReview = () => {
    setShowReviewForm(true);
  };

  const goBack = () => {
    navigate(-1);
  };

  // Debounced search for suggestions
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (hasUserInteracted && searchName.trim().length >= 2) {
        fetchSuggestions(searchName.trim());
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchName, hasUserInteracted]);

  const fetchSuggestions = async (query) => {
    setSuggestionLoading(true);
    try {
      const response = await axios.get(`${API}/therapists/search?name=${encodeURIComponent(query)}&limit=8`);
      setSuggestions(response.data.therapists || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleSuggestionClick = (therapist) => {
    console.log('🖱️ Suggestion clicked, navigating to therapist:', therapist.id);
    // Immediately clear all suggestion state
    setSearchName('');
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    if (typeof setLoading === 'function') setLoading(false);
    if (typeof setSuggestionLoading === 'function') setSuggestionLoading(false);
    
    // Navigate after a brief delay to ensure state is cleared
    setTimeout(() => {
      navigate(`/therapist/${therapist.id}`);
    }, 10);
  };

  const handleInputKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for click events
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!searchName.trim() && !searchCity.trim() && selectedSpecialties.length === 0) {
      alert('Please enter a therapist name, city, or select specialties to search');
      return;
    }

    // Track search event
    trackTherapistReviewsEvents.searchTherapist(searchName.trim(), searchCity.trim());

    // Navigate to search results page with URL parameters
    const params = new URLSearchParams();
    if (searchName.trim()) params.append('name', searchName.trim());
    if (searchCity.trim()) params.append('city', searchCity.trim());
    if (selectedSpecialties && selectedSpecialties.length > 0) {
      selectedSpecialties.forEach(specialty => params.append('specialty', specialty));
    }
    
    navigate(`/search?${params.toString()}`);
  };

  // Wait for auth to load before rendering
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading therapist profile...</p>
        </div>
      </div>
    );
  }

  if (!therapist) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Therapist not found</p>
          <button
            onClick={goBack}
            className="mt-4 text-[#3B82F6] hover:text-[#2563EB]"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity min-w-0" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="Therapist Reviews Logo" className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0" />
              <h1 className="text-base sm:text-2xl font-bold text-gray-900 whitespace-nowrap">Therapist Reviews</h1>
            </div>
            {user ? (
              <UserAvatar user={user} showDropdown={true} />
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="bg-[#3B82F6] text-white px-3 sm:px-4 py-2 rounded-2xl hover:bg-[#2563EB] transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-6 mb-4 sm:mb-6">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => {
                    setHasUserInteracted(true);
                    setSearchName(e.target.value);
                  }}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleInputBlur}
                  onFocus={() => {
                    setHasUserInteracted(true);
                    if (suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="Search therapist name..."
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 text-base sm:text-lg border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-colors"
                  autoComplete="off"
                />

                {/* Suggestions Dropdown */}
                {showSuggestions && (suggestions.length > 0 || suggestionLoading) && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-lg z-50 mt-1 max-h-80 overflow-y-auto">
                    {suggestionLoading && (
                      <div className="p-4 text-center text-gray-500">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        <span className="ml-2">Searching...</span>
                      </div>
                    )}

                    {!suggestionLoading && suggestions.map((therapistSuggestion, index) => (
                      <div
                        key={therapistSuggestion.id}
                        onClick={() => handleSuggestionClick(therapistSuggestion)}
                        className={`p-4 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                          index === selectedIndex ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{therapistSuggestion.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="inline-flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {therapistSuggestion.city}
                              </span>
                            </p>
                            {therapistSuggestion.specialties && therapistSuggestion.specialties.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {therapistSuggestion.specialties.slice(0, 3).join(', ')}
                                {therapistSuggestion.specialties.length > 3 && '...'}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="flex items-center space-x-1">
                              <StarRating rating={therapistSuggestion.avg_overall} size="small" />
                              <span className="text-sm text-gray-500">
                                ({therapistSuggestion.total_reviews})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {!suggestionLoading && suggestions.length === 0 && searchName.trim().length >= 2 && (
                      <div className="p-4 text-center text-gray-500">
                        No therapists found matching "{searchName}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="Search city..."
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 text-base sm:text-lg border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-colors"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-2 sm:px-8 sm:py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-2xl transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Search</span>
              </button>
            </div>
          </form>
        </div>

        {/* Therapist Info */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 mb-4 sm:mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:space-x-8">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{therapist.name}</h1>
              <div className="flex items-center text-gray-600 mb-4">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {therapist.city}
              </div>
              {therapist.specialties && therapist.specialties.length > 0 && (
                <div className="mb-4">
                  <span className="text-gray-600 font-semibold text-lg">Specialties: </span>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {therapist.specialties.map((specialty, index) => (
                      <span key={index} className="inline-block bg-blue-100 text-blue-800 text-base px-4 py-2 rounded-full">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 md:mt-0 md:w-80">
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Rating</h3>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-3xl font-bold text-gray-900">{therapist.avg_overall.toFixed(1)}</div>
                  <div>
                    <StarRating rating={therapist.avg_overall} size="large" />
                    <p className="text-sm text-gray-600 mt-1">{therapist.total_reviews} reviews</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700" style={{minWidth: '78px', flexShrink: 0, whiteSpace: 'nowrap'}}>Trust & Comfort</span>
                    <div className="flex items-center space-x-2" style={{marginLeft: '8px'}}>
                      <StarRating rating={therapist.avg_trust_safety || 0} size="small" />
                      <span className="text-sm text-gray-600">{(therapist.avg_trust_safety || 0).toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700" style={{minWidth: '78px', flexShrink: 0, whiteSpace: 'nowrap'}}>Helpfulness</span>
                    <div className="flex items-center space-x-2" style={{marginLeft: '8px'}}>
                      <StarRating rating={therapist.avg_helpfulness} size="small" />
                      <span className="text-sm text-gray-600">{therapist.avg_helpfulness.toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700" style={{minWidth: '78px', flexShrink: 0, whiteSpace: 'nowrap'}}>Knowledge</span>
                    <div className="flex items-center space-x-2" style={{marginLeft: '8px'}}>
                      <StarRating rating={therapist.avg_knowledge} size="small" />
                      <span className="text-sm text-gray-600">{therapist.avg_knowledge.toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700" style={{minWidth: '78px', flexShrink: 0, whiteSpace: 'nowrap'}}>Punctuality</span>
                    <div className="flex items-center space-x-2" style={{marginLeft: '8px'}}>
                      <StarRating rating={therapist.avg_punctuality} size="small" />
                      <span className="text-sm text-gray-600">{therapist.avg_punctuality.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleWriteReview}
                  className="w-full mt-6 bg-[#3B82F6] text-white px-4 py-2 rounded-2xl font-semibold hover:bg-[#2563EB] transition-colors"
                >
                  Write a Review
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Patient Reviews</h2>
          </div>
          
          {reviews.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-gray-500">No reviews yet. Be the first to review this therapist!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-semibold text-gray-900">Patient</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm text-gray-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600" style={{minWidth: '120px', whiteSpace: 'nowrap'}}>Trust & Comfort:</span>
                          <StarRating rating={review.trust_safety_rating || 0} size="small" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600" style={{minWidth: '120px', whiteSpace: 'nowrap'}}>Helpfulness:</span>
                          <StarRating rating={review.helpfulness_rating} size="small" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600" style={{minWidth: '120px', whiteSpace: 'nowrap'}}>Knowledge:</span>
                          <StarRating rating={review.knowledge_rating} size="small" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600" style={{minWidth: '120px', whiteSpace: 'nowrap'}}>Punctuality:</span>
                          <StarRating rating={review.punctuality_rating} size="small" />
                        </div>
                      </div>
                      
                      {review.comment && (
                        <p className="text-gray-700 mt-3">{review.comment}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
        <ReviewForm
          therapistId={therapist.id}
          therapistName={therapist.name}
          onClose={() => setShowReviewForm(false)}
          onReviewSubmitted={() => {
            setShowReviewForm(false);
            fetchTherapistData();
          }}
        />
      )}
    </div>
  );
};

// Review Form Component
const ReviewForm = ({ therapistId, therapistName, onClose, onReviewSubmitted }) => {
  const [trustSafetyRating, setTrustSafetyRating] = useState(0);
  const [helpfulnessRating, setHelpfulnessRating] = useState(0);
  const [knowledgeRating, setKnowledgeRating] = useState(0);
  const [punctualityRating, setPunctualityRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if form meets submission requirements
  const isFormValid = () => {
    // All four ratings must be selected
    const allRatingsSelected = trustSafetyRating > 0 && helpfulnessRating > 0 && knowledgeRating > 0 && punctualityRating > 0;
    
    // If comment exists, it must be at least 50 characters
    const commentValid = comment.trim().length === 0 || comment.trim().length >= 50;
    
    return allRatingsSelected && commentValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated before submitting
    if (!user) {
      alert('Please sign in or create an account to submit your review.');
      // Redirect to login page
      navigate('/login');
      return;
    }

    // Validate that all ratings are selected
    if (trustSafetyRating === 0 || helpfulnessRating === 0 || knowledgeRating === 0 || punctualityRating === 0) {
      alert('Please provide star ratings for all four categories: Trust & Comfort, Helpfulness, Knowledge, and Punctuality.');
      return;
    }

    // Validate comment length if comment is provided
    if (comment.trim().length > 0 && comment.trim().length < 50) {
      alert('Comments must be at least 50 characters long. You can also leave the comment section blank if you prefer to submit only ratings.');
      return;
    }
    
    // Track review start
    trackTherapistReviewsEvents.startReview(therapistId);
    
    setSubmitting(true);

    try {
      const headers = {};
      
      // Add appropriate auth header
      const emailToken = localStorage.getItem('access_token');
      const sessionId = localStorage.getItem('sessionId');
      
      if (emailToken) {
        headers['Authorization'] = `Bearer ${emailToken}`;
      } else if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }

      await axios.post(`${API}/reviews`, {
        therapist_id: therapistId,
        trust_safety_rating: trustSafetyRating,
        helpfulness_rating: helpfulnessRating,
        knowledge_rating: knowledgeRating,
        punctuality_rating: punctualityRating,
        comment: comment.trim()
      }, { headers });

      // Track successful review submission
      const overallRating = Math.round((trustSafetyRating + helpfulnessRating + knowledgeRating + punctualityRating) / 4);
      trackTherapistReviewsEvents.submitReview(therapistId, overallRating);

      alert('Anonymous review submitted successfully!');
      onReviewSubmitted();
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Error submitting review. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const RatingInput = ({ label, value, onChange }) => {
    const [hoverValue, setHoverValue] = useState(0);

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() => onChange(rating)}
              onMouseEnter={() => setHoverValue(rating)}
              onMouseLeave={() => setHoverValue(0)}
              className="w-8 h-8 transition-colors duration-150 focus:outline-none"
            >
              <svg
                className={`w-8 h-8 fill-current`}
                style={{color: rating <= (hoverValue > 0 ? hoverValue : value) ? '#f6af3b' : '#d1d5db'}}
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 my-4 sm:my-0 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-900 pr-2">Review for {therapistName}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Anonymity Notice */}

        <form onSubmit={handleSubmit}>
          <RatingInput
            label="Trust & Comfort"
            value={trustSafetyRating}
            onChange={setTrustSafetyRating}
          />
          
          <RatingInput
            label="Helpfulness"
            value={helpfulnessRating}
            onChange={setHelpfulnessRating}
          />
          
          <RatingInput
            label="Knowledge"
            value={knowledgeRating}
            onChange={setKnowledgeRating}
          />
          
          <RatingInput
            label="Punctuality"
            value={punctualityRating}
            onChange={setPunctualityRating}
          />

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Comment (Optional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-xl focus:ring-2 focus:border-transparent ${
                comment.trim().length > 0 && comment.trim().length < 50
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Share your experience..."
            />
            {comment.trim().length > 0 && (
              <div className="mt-2 text-sm">
                <span className={`${
                  comment.trim().length < 50 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {comment.trim().length}/50 characters
                </span>
                {comment.trim().length < 50 && (
                  <span className="text-gray-500 ml-2">
                    (Minimum 50 characters required)
                  </span>
                )}
              </div>
            )}
            <div className="mt-1 text-xs text-gray-500">
              Comments are optional, but if provided, must be at least 50 characters long.
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !isFormValid()}
              className="flex-1 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Account Settings Page
const AccountSettingsPage = () => {
  const { user, logout, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check URL parameters to set initial tab
    const tab = searchParams.get('tab');
    if (tab === 'reviews') {
      setActiveTab('reviews');
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
      // Fetch reviews when user is loaded
      if (activeTab === 'reviews') {
        fetchMyReviews();
      }
    }
  }, [user, activeTab]);

  const fetchMyReviews = async () => {
    setReviewsLoading(true);
    setReviewsError('');
    try {
      const token = localStorage.getItem('access_token');
      const sessionId = localStorage.getItem('sessionId');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }

      const response = await axios.get(`${API}/reviews/my-reviews`, { headers });
      setReviews(response.data.reviews || []);
    } catch (err) {
      setReviewsError('Failed to load your reviews');
      console.error('Error fetching reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    window.location.href = '/login';
    return null;
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    // Validate password fields if provided
    if (user.auth_type === 'email' && formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setError('New passwords do not match');
        setIsLoading(false);
        return;
      }
      if (formData.newPassword.length < 8) {
        setError('New password must be at least 8 characters long');
        setIsLoading(false);
        return;
      }
      if (!formData.currentPassword) {
        setError('Current password is required to change password');
        setIsLoading(false);
        return;
      }
    }

    try {
      const token = localStorage.getItem('access_token');
      const sessionId = localStorage.getItem('sessionId');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }

      // Update profile information
      await axios.put(`${API}/auth/update-profile`, {
        name: formData.name,
        email: formData.email
      }, { headers });

      // Change password if provided
      if (user.auth_type === 'email' && formData.newPassword) {
        await axios.put(`${API}/auth/change-password`, {
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        }, { headers });
        
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }

      setMessage('Account information updated successfully');
      // Refresh auth status
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update account information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setIsLoading(false);
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Password change is only available for email accounts');
        setIsLoading(false);
        return;
      }

      const response = await axios.put(`${API}/auth/change-password`, {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        setMessage('Password changed successfully');
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    if (window.confirm('Are you sure you want to unlink your Google account? You will need to use email login in the future.')) {
      try {
        setIsLoading(true);
        const sessionId = localStorage.getItem('sessionId');
        
        await axios.post(`${API}/auth/unlink-google`, {}, {
          headers: { 'X-Session-ID': sessionId }
        });
        
        setMessage('Google account unlinked successfully. You will be redirected to set up email login.');
        setTimeout(() => {
          logout();
        }, 2000);
      } catch (err) {
        setError('Failed to unlink Google account');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="Therapist Reviews Logo" className="w-8 h-8" />
              <h1 className="text-xl font-semibold text-gray-900">Therapist Reviews</h1>
            </div>
            <UserAvatar user={user} showDropdown={true} />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('account')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'account'
                    ? 'border-[#3B82F6] text-[#3B82F6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Account Information
              </button>
              <button
                onClick={() => {
                  setActiveTab('reviews');
                  if (reviews.length === 0 && !reviewsLoading) {
                    fetchMyReviews();
                  }
                }}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'reviews'
                    ? 'border-[#3B82F6] text-[#3B82F6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Reviews
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Account Information Tab */}
            {activeTab === 'account' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Account Information</h2>
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  {/* Hidden name field - kept for backend compatibility but not shown in UI */}
                  <input type="hidden" value={formData.name} />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                  </div>

                  {user.auth_type === 'email' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                        <input
                          type="password"
                          value={formData.currentPassword}
                          onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                        <input
                          type="password"
                          value={formData.newPassword}
                          onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                        <input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6]"
                        />
                      </div>
                    </>
                  )}

                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-medium text-gray-900 mb-2">Account Type</h3>
                    <div className="flex items-center space-x-3">
                      <span className="inline-block px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                        {user.auth_type === 'google' ? 'Google Account' : 'Email Account'}
                      </span>
                      {user.auth_type === 'google' && (
                        <button
                          type="button"
                          onClick={handleUnlinkGoogle}
                          disabled={isLoading}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          Unlink Google Account
                        </button>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-6 py-2 rounded-xl font-medium disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={logout}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-xl font-medium transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}

            {/* My Reviews Tab */}
            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-6">My Reviews</h2>
                
                {reviewsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
                  </div>
                ) : reviewsError ? (
                  <div className="text-center py-12">
                    <p className="text-red-600">{reviewsError}</p>
                  </div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                    <p className="text-gray-500 mb-6">You haven't written any reviews yet.</p>
                    <a href="/" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-6 py-2 rounded-xl font-medium transition-colors">
                      Browse Therapists
                    </a>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border border-gray-200 rounded-2xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-gray-900">{review.therapist_name}</h3>
                            <p className="text-sm text-gray-500">{review.therapist_city}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">{new Date(review.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Trust & Comfort</p>
                            <div className="flex items-center justify-center">
                              <StarRating rating={review.trust_safety_rating || 0} size="small" />
                              <span className="text-xs text-gray-600 ml-1">{review.trust_safety_rating || 0}</span>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Helpfulness</p>
                            <div className="flex items-center justify-center">
                              <StarRating rating={review.helpfulness_rating} size="small" />
                              <span className="text-xs text-gray-600 ml-1">{review.helpfulness_rating}</span>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Knowledge</p>
                            <div className="flex items-center justify-center">
                              <StarRating rating={review.knowledge_rating} size="small" />
                              <span className="text-xs text-gray-600 ml-1">{review.knowledge_rating}</span>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Punctuality</p>
                            <div className="flex items-center justify-center">
                              <StarRating rating={review.punctuality_rating} size="small" />
                              <span className="text-xs text-gray-600 ml-1">{review.punctuality_rating}</span>
                            </div>
                          </div>
                        </div>
                        
                        {review.comment && (
                          <div className="bg-gray-50 rounded-xl p-4">
                            <p className="text-gray-700 text-sm">{review.comment}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            {message && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-green-800 text-sm">{message}</p>
              </div>
            )}

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// My Reviews Page
const MyReviewsPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      fetchMyReviews();
    }
  }, [user]);

  const fetchMyReviews = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const sessionId = localStorage.getItem('sessionId');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }

      const response = await axios.get(`${API}/reviews/my-reviews`, { headers });
      setReviews(response.data.reviews || []);
    } catch (err) {
      setError('Failed to load your reviews');
      console.error('Error fetching reviews:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    window.location.href = '/login';
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="Therapist Reviews Logo" className="w-8 h-8" />
              <h1 className="text-xl font-semibold text-gray-900">Therapist Reviews</h1>
            </div>
            <UserAvatar user={user} showDropdown={true} />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">My Reviews</h1>
            <p className="text-gray-600 mt-2">View all your anonymous reviews</p>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                <p className="text-gray-500 mb-6">You haven't written any reviews yet.</p>
                <a href="/" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-6 py-2 rounded-xl font-medium transition-colors">
                  Browse Therapists
                </a>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.id} className="border border-gray-200 rounded-2xl p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">{review.therapist_name}</h3>
                        <p className="text-sm text-gray-500">{review.therapist_city}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">{new Date(review.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Trust & Safety</p>
                        <div className="flex items-center justify-center">
                          <StarRating rating={review.trust_safety_rating || 0} size="small" />
                          <span className="text-xs text-gray-600 ml-1">{review.trust_safety_rating || 0}</span>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Helpfulness</p>
                        <div className="flex items-center justify-center">
                          <StarRating rating={review.helpfulness_rating} size="small" />
                          <span className="text-xs text-gray-600 ml-1">{review.helpfulness_rating}</span>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Knowledge</p>
                        <div className="flex items-center justify-center">
                          <StarRating rating={review.knowledge_rating} size="small" />
                          <span className="text-xs text-gray-600 ml-1">{review.knowledge_rating}</span>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Punctuality</p>
                        <div className="flex items-center justify-center">
                          <StarRating rating={review.punctuality_rating} size="small" />
                          <span className="text-xs text-gray-600 ml-1">{review.punctuality_rating}</span>
                        </div>
                      </div>
                    </div>
                    
                    {review.comment && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-gray-700 text-sm">{review.comment}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Page Component (for auth redirect)
const ProfilePage = () => {
  const { user, loading, checkAuthStatus } = useAuth();

  useEffect(() => {
    console.log('ProfilePage loaded, checking auth...');
    console.log('Current URL:', window.location.href);
    console.log('Hash:', window.location.hash);
    
    // Force auth check when profile page loads
    checkAuthStatus();
  }, [checkAuthStatus]);

  useEffect(() => {
    console.log('User state changed:', { user, loading });
    if (!loading && user) {
      console.log('User authenticated, redirecting to home...');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    }
  }, [user, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Setting up your anonymous session...</p>
          <p className="text-xs text-gray-400 mt-2">Hash: {window.location.hash}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h2>
        <p className="text-gray-600 mb-4">Your anonymous session is ready. You can now submit reviews completely anonymously.</p>
        <p className="text-sm text-gray-500">Redirecting to home page...</p>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
        <Router>
          <GATracker />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/register" element={<RegistrationPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/therapist/:id" element={<TherapistProfileWrapper />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/account" element={<AccountSettingsPage />} />
            <Route path="/my-reviews" element={<MyReviewsPage />} />
          </Routes>
        </Router>
    </AuthProvider>
  );
};

// Wrapper component to extract therapist ID from URL
const TherapistProfileWrapper = () => {
  const { id } = useParams();
  return <TherapistProfile therapistId={id} />;
};

export default App;